import { Channel } from '../../consts/channels'

declare global {
  interface Window {
    jlData: any
    monetateQ: {
      push: Function
    }
    hideSalesLiveChat?: Boolean
    auth0?: any
    embeddedservice_bootstrap?: any
    embedded_svc?: any
    newrelic: any
  }
  interface Event {
    detail: any
  }
}

export enum BotType {
  AGENT_FORCE = 'AgentForce',
  DEFAULT = 'Default',
  LIVE_CHAT = 'LiveChat',
}

export type MiawConfig = {
  siteURL: string
  orgID: string
  eswConfigDevName: string
  snippetConfigURL: string
}

export const agentNamesNotTracked = [
  'Chat',
  'JL Refunds',
  'JL Returns',
  'JL Delivery',
  'JL Customer Service',
  'JL Customer Service Team',
  'JL Technical Support Team',
  'JL Placing or Amending orders',
  'My John Lewis',
  'Verify ID',
]

export const ALL_CATEGORIES = [
  'home_appliances',
  'tv_audio',
  'computing_mobile',
  'home',
  'beauty',
  'fashion',
  'christmas_gifts',
] as const
type AllCategoryTuple = typeof ALL_CATEGORIES
type TargetCategories = AllCategoryTuple[number]

export type CategoryQueue = TargetCategories | null

export type ChatTargetDemographic = Partial<
  Record<
    TargetCategories,
    { isSessionWithinTargetPercentage: boolean; targetPercentage: number; withinOpenHours: boolean }
  >
>

export type BrandTargetSet = {
  partialUrls: string[]
  categoryApplicableForQueue: CategoryQueue
}

export type SearchTermCriteria = {
  categoryApplicableForQueue: CategoryQueue
  terms: string[]
}

//@TODO: type this
export type EmbeddedSVC = any | undefined
export type EmbeddedServiceBootstrap = any | undefined

export type BreadCrumb = string | { value: string }

export type SubscribedLiveChatListeners = {
  [key in LiveChatEvents]?: (data: any) => void
}

export type ChatInitializationStoredConfig = {
  channel: Channel
  buttonId: string
  eswLiveAgentDevName: string
  categoryApplicableForQueue: CategoryQueue
}

export type MonitoredChatData = {
  transferJourney: string[]
  originalChannelOrCategory: CategoryQueue | Channel
  chatKey: string
  initialQueuePosition?: number
  initialQueuePositionTime?: null | number
}

/**
 * Chat Event Notifications
 * Full list: https://developer.salesforce.com/docs/atlas.en-us.snapins_web_dev.meta/snapins_web_dev/snapins_web_chat_events.htm
 * @readonly
 * @enum {string}
 */
export enum LiveChatEvents {
  /**
   * Fired when an agent joins the chat conference.
   */
  onAgentJoinedConference = 'onAgentJoinedConference',
  /**
   * Fired when an agent leaves the chat conference.
   */
  onAgentLeftConference = 'onAgentLeftConference',
  /**
   * Fired when the agent sends a message.
   */
  onAgentMessage = 'onAgentMessage',
  /**
   * Fired when the chatbot sends a rich message. The chatbot sends a mixture of rich messages and plain messages.
   */
  onAgentRichMessage = 'onAgentRichMessage',
  /**
   * Fired when the chat visitor sends a message.
   */
  onChasitorMessage = 'onChasitorMessage',
  /**
   * Fired when the chat conference has ended.
   */
  onChatConferenceEnded = 'onChatConferenceEnded',
  /**
   * Fired when the chat conference is initiated.
   */
  onChatConferenceInitiated = 'onChatConferenceInitiated',
  /**
   * Fired when the agent ends the chat.
   */
  onChatEndedByAgent = 'onChatEndedByAgent',
  /**
   * Fired when the chat visitor ends the chat.
   */
  onChatEndedByChasitor = 'onChatEndedByChasitor',
  /**
   * Fired when the chat reconnects successfully.
   */
  onChatReconnectSuccessful = 'onChatReconnectSuccessful',
  /**
   * Fired when the chat request is successful.
   */
  onChatRequestSuccess = 'onChatRequestSuccess',
  /**
   * Fired when a chat transfer is successful.
   */
  onChatTransferSuccessful = 'onChatTransferSuccessful',
  /**
   * Fired when the connection to the agent is lost.
   */
  onConnectionError = 'onConnectionError',
  /**
   * Fired when the visitor responds after an idle timeout warning is visible, which will clear the warning.
   */
  onIdleTimeoutClear = 'onIdleTimeoutClear',

  /**
   * Fired in the following scenarios:
   * - The visitor has requested a chat and is waiting for an agent.
   * - After requesting a chat, the visitor navigates to another page, but is still waiting for an agent.
   * - The visitor had previously lost connection (see reconnectingState) and has regained it.
   * - The visitor has advanced in the queue, but is still waiting for an agent to accept the chat request.
   * - A bot or agent has requested a chat transfer and is waiting for another agent to accept.
   * - During a chat transfer, the visitor navigates to another page, but is still waiting to be transferred.
   * - The visitor has advanced in the queue, but is still waiting for an agent to accept the chat transfer.
   */
  onQueueUpdate = 'onQueueUpdate',

  // ----------------------------------------------
  // The following general application-level events to the client are not specific to a Chat session, but can be used during Chat.
  // These events do not broadcast any data.
  // ----------------------------------------------
  /**
   * Callback to fire when the help button is clicked.
   */
  onHelpButtonClick = 'onHelpButtonClick',
  /**
   * Fired when Embedded Service Chat has ended and the application is closed.
   */
  afterDestroy = 'afterDestroy',
  /**
   * Fired when the Embedded Service Chat application is maximized by the end user clicking the minimized state.
   */
  afterMaximize = 'afterMaximize',
  /**
   * Fired when the Embedded Service Chat application is minimized by the end user clicking the minimize button in the chat window header.
   */
  afterMinimize = 'afterMinimize',
  /**
   * Click handler for the "Submit" button in the offline support UI component.
   */
  onClickSubmitButton = 'onClickSubmitButton',
  /**
   * Accepts the automated chat invitation.
   */
  onInviteAccepted = 'onInviteAccepted',
  /**
   * Rejects the automated chat invitation.
   */
  onInviteRejected = 'onInviteRejected',
  /**
   * Fired after the getSettings call is completed. The call happens on page load and retrieves settings for the chat button requested. Includes the initial agent availability status of the button on the snippet.
   */
  onSettingsCallCompleted = 'onSettingsCallCompleted',

  // Below 3 events are not listed in the docs given by Salesforce, but are mentioned in the exemples
  onChatEstablished = 'onChatEstablished',
  onIdleTimeoutOccurred = 'onIdleTimeoutOccurred',
  onAvailability = 'onAvailability',

  //@TODO: these events seem applicable to non-Omni channel bot configurations. More investigation needed
  // //	Fired when the visitor has not responded to the agent during the time set in the Customer Time-out Warning (seconds) field in Chat Buttons & Invitations Setup.
  // onIdleTimeoutWarningStart = 'onIdleTimeoutWarningStart',
  // //	Fired after the automated chat invitation static resource is loaded.
  // onInvitationResourceLoaded = 'onInvitationResourceLoaded',
  // onChatbotEndedChat = 'onChatbotEndedChat',
  // chasitorAgentChatEnded = 'chasitorAgentChatEnded',
  // chasitorAgentChatMessage = 'chasitorAgentChatMessage',
  // chasitorAgentChatTransferred = 'chasitorAgentChatTransferred',
  // transferToBotInitiated = 'transferToBotInitiated',
  // transferToButtonInitiated = 'transferToButtonInitiated',
  // transferToQueueInitiated = 'transferToQueueInitiated',
  // transferToSbrSkillInitiated = 'transferToSbrSkillInitiated',
  // chasitorAgentDisconnected = 'chasitorAgentDisconnected',
  // chasitorAgentJoinedConference = 'chasitorAgentJoinedConference',
  // chasitorAgentLeftConference = 'chasitorAgentLeftConference',
  // agentRichMessage = 'agentRichMessage',
  // chasitorAgentTypingUpdate = 'chasitorAgentTypingUpdate',
  // chasitorBotChatTransferred = 'chasitorBotChatTransferred',
  // chasitorChasitorChatCanceled = 'chasitorChasitorChatCanceled',
  // chasitorChasitorChatEnded = 'chasitorChasitorChatEnded',
  // chasitorChasitorChatMessage = 'chasitorChasitorChatMessage',
  // chasitorIdleTimeout = 'chasitorIdleTimeout',
  // chatbotEndedChat = 'chatbotEndedChat',
  // chasitorChatEstablished = 'chasitorChatEstablished',
  // chasitorChatRequestFailed = 'chasitorChatRequestFailed',
  // chasitorChatRequestSuccessful = 'chasitorChatRequestSuccessful',
  // chatResumedAfterTransfer = 'chatResumedAfterTransfer',
  // clearChasitorIdleTimeout = 'clearChasitorIdleTimeout',
  // chasitorConnectionError = 'chasitorConnectionError',
  // deploymentSettings = 'deploymentSettings',
  // chasitorEstimatedWaitTime = 'chasitorEstimatedWaitTime',
  // fileTransferCanceled = 'fileTransferCanceled',
  // fileTransferFailure = 'fileTransferFailure',
  // fileTransferRequested = 'fileTransferRequested',
  // fileTransferSuccess = 'fileTransferSuccess',
  // postChat = 'postChat',
  // chasitorQueueUpdate = 'chasitorQueueUpdate',
  // chasitorReconnecting = 'chasitorReconnecting',
  // startChasitorIdleWarning = 'startChasitorIdleWarning',
  // updateChasitorIdleWarning = 'updateChasitorIdleWarning',
}
